/**
 * Required parking stall sizes for one scope of the zoning research — a jurisdiction (the usual
 * place), a zone/overlay (override), or the firm's fallback. Shown in Permitting → Zones &
 * Jurisdictions; the Modeler reads the resolved sizes so cars draw the required stall on plans.
 * Width/length are typed as lengths (9', 8'-6", 18) — blank both to clear a type.
 */
import { useEffect, useState } from 'react'
import { STALL_TYPES, listStallSizes, setStallSize } from '../services/parkingStalls.js'
import { alertDialog } from './Dialog.jsx'

/** 9'-6" style feet-inches (to the 1/2") */
function formatFtIn(v) {
  const totalIn = Math.round(v * 24) / 2
  const ft = Math.floor(totalIn / 12)
  const inch = totalIn - ft * 12
  return inch ? `${ft}'-${inch}"` : `${ft}'`
}
/** 9' · 9'-6" · 9 6 · 9.5 · 114" → feet (null when unreadable) */
function parseLength(t) {
  const s = String(t).trim().replace(/[′’]/g, "'").replace(/[″”]/g, '"')
  let m = s.match(/^(\d+(?:\.\d+)?)\s*'?\s*-?\s*(?:(\d+(?:\.\d+)?)\s*"?)?$/)
  if (m && (s.includes("'") || m[2] == null)) return Number(m[1]) + (m[2] ? Number(m[2]) / 12 : 0)
  m = s.match(/^(\d+(?:\.\d+)?)\s*"$/)
  if (m) return Number(m[1]) / 12
  m = s.match(/^(\d+)\s+(\d+(?:\.\d+)?)$/)
  if (m) return Number(m[1]) + Number(m[2]) / 12
  return null
}
const fmt = (v) => (typeof v === 'number' && v > 0 ? formatFtIn(v) : '')

export default function ParkingStallsPanel({ scope, scopeId = null, title, hint, byName }) {
  const [rows, setRows] = useState(null)
  const [saving, setSaving] = useState('')

  useEffect(() => {
    setRows(null)
    listStallSizes(scope, scopeId).then(setRows).catch(() => setRows({}))
  }, [scope, scopeId])

  async function save(type, patch) {
    const cur = rows?.[type] || {}
    const next = { ...cur, ...patch }
    const complete = next.widthFt > 0 && next.lengthFt > 0
    // half-entered and never saved: hold it locally until both numbers are there
    if (!complete && !cur.updatedAt) { setRows((r) => ({ ...r, [type]: next })); return }
    setSaving(type)
    try {
      const row = await setStallSize(scope, scopeId, type, next, byName)
      setRows((r) => {
        const o = { ...r }
        if (row) o[type] = row
        else delete o[type]
        return o
      })
    } catch (e) {
      await alertDialog({ title: 'Not saved', message: `Could not save the ${type} stall size: ${e?.message || 'unknown error'}` })
    }
    setSaving('')
  }

  const lenField = (type, key, placeholder) => {
    const cur = rows?.[type]?.[key]
    return (
      <input
        key={type + key + (cur ?? '')}
        style={S.input} placeholder={placeholder} defaultValue={fmt(cur)}
        onBlur={(e) => {
          const t = e.target.value.trim()
          const v = t ? parseLength(t) : null
          if (t && (v == null || v < 0)) { e.target.value = fmt(cur); return }
          if ((v ?? null) === (cur ?? null)) return
          save(type, { [key]: v })
        }}
      />
    )
  }

  return (
    <div style={{ marginTop: 20 }}>
      <div style={S.title}>{title}</div>
      {hint && <div style={S.hint}>{hint}</div>}
      {rows == null ? <div style={S.hint}>Loading…</div> : (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={S.th}>Stall</th><th style={S.th}>Width</th><th style={S.th}>Length</th>
              <th style={S.th}>Aisle</th><th style={S.th}>Code section</th>
            </tr>
          </thead>
          <tbody>
            {STALL_TYPES.map(({ value, label }) => (
              <tr key={value}>
                <td style={S.td}>{label}{saving === value && <span style={S.saving}> saving…</span>}</td>
                <td style={S.td}>{lenField(value, 'widthFt', "9'")}</td>
                <td style={S.td}>{lenField(value, 'lengthFt', "18'")}</td>
                <td style={S.td}>{value === 'accessible' ? lenField(value, 'aisleFt', "5'") : <span style={S.hint}>—</span>}</td>
                <td style={S.td}>
                  <input
                    key={value + 'ref' + (rows?.[value]?.codeReference || '')}
                    style={S.input} placeholder="e.g. LBMC 25.52.012"
                    defaultValue={rows?.[value]?.codeReference || ''}
                    disabled={!rows?.[value]?.widthFt}
                    onBlur={(e) => {
                      const v = e.target.value.trim()
                      if (v !== (rows?.[value]?.codeReference || '')) save(value, { codeReference: v })
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

const S = {
  title: { fontFamily: 'Montserrat, sans-serif', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-mid, #555)', marginBottom: 4 },
  hint: { fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--aj-text-muted, #888)', marginBottom: 8 },
  th: { textAlign: 'left', fontFamily: 'DM Sans, sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--aj-text-muted, #888)', padding: '4px 6px', borderBottom: '0.5px solid var(--aj-border, #ddd)' },
  td: { padding: '4px 6px', fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--aj-text, #222)', borderBottom: '0.5px solid var(--aj-border, #eee)', verticalAlign: 'middle' },
  input: { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Mono", monospace', fontSize: 12, padding: '4px 6px', border: '0.5px solid var(--aj-border-mid, #ccc)', borderRadius: 4, background: 'var(--aj-bg-surface, #fff)', color: 'var(--aj-text, #222)' },
  saving: { fontSize: 11, color: 'var(--aj-text-muted, #888)' },
}
